import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";
import { ObImportStep } from "@/components/ob/ObImportStep";
import { ObProjectionTB, ObProjectionBS, ObProjectionPL } from "@/components/ob/ObProjection";
import {
  Building2, CalendarRange, Users, HardHat, Package, Briefcase, Landmark,
  Layers, AlertTriangle, Scale, FileBarChart2, TrendingUp, ShieldCheck,
  CheckCircle2, ChevronRight, PlayCircle, RotateCcw, X,
} from "lucide-react";

const STEPS = [
  { key: "company", n: 1, label: "Company Information", icon: Building2 },
  { key: "cutoff", n: 2, label: "Cutoff Date", icon: CalendarRange },
  { key: "debtors", n: 3, label: "Import Debtors", icon: Users, category: true },
  { key: "creditors", n: 4, label: "Import Creditors", icon: HardHat, category: true },
  { key: "inventory", n: 5, label: "Import Inventory", icon: Package, category: true },
  { key: "assets", n: 6, label: "Import Assets", icon: Briefcase, category: true },
  { key: "bank", n: 7, label: "Import Bank & Cash", icon: Landmark, category: true },
  { key: "other", n: 8, label: "Import Other Balances", icon: Layers, category: true },
  { key: "exceptions", n: 9, label: "Review Exceptions", icon: AlertTriangle },
  { key: "tb", n: 10, label: "Projected Trial Balance", icon: Scale },
  { key: "bs", n: 11, label: "Projected Balance Sheet", icon: FileBarChart2 },
  { key: "pnl", n: 12, label: "Projected P&L", icon: TrendingUp },
  { key: "post", n: 13, label: "Approve & Post", icon: ShieldCheck },
];

const inputCls = "border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#F97316] bg-white";
const btnPrimary = "bg-[#0B1F3A] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#13294B] disabled:opacity-50";
const btnOrange = "bg-orange-600 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-orange-700 disabled:opacity-50";

export default function OpeningBalanceWizard() {
  const { hasPerm, user } = useAuth();
  const [batch, setBatch] = useState(null);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("company");
  const [newName, setNewName] = useState("Opening Balance Migration");
  const canEdit = user?.role === "admin" || hasPerm("opening_balance", "edit");
  const canApprove = user?.role === "admin" || hasPerm("opening_balance", "approve");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/opening-balance/batch");
      setBatch(r.data.batch);
      setCompany(r.data.company || {});
    } catch { toast.error("Failed to load migration batch"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function createBatch() {
    try {
      await api.post("/opening-balance/batch", { name: newName });
      toast.success("Migration batch created — progress saves automatically");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create batch"); }
  }

  const stepDone = (key) => {
    if (!batch) return false;
    if (key === "post") return batch.status === "posted";
    if (["exceptions", "tb", "bs", "pnl"].includes(key)) return batch.status === "posted";
    return (batch.steps || {})[key]?.status === "done";
  };
  const doneCount = STEPS.filter((s) => stepDone(s.key)).length;
  const locked = batch && !["draft", "validated"].includes(batch.status);

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="ob-wizard">
      <PageHeader
        title="Opening Balance Migration"
        subtitle="13-step guided wizard — dry-run projections, OBC=0 gate, maker/checker posting. Progress saves automatically."
      />

      {!batch ? (
        <div className="bg-white border border-slate-200 p-8 max-w-xl" data-testid="ob-start-card">
          <div className="text-sm font-bold text-slate-800 mb-1">No active migration batch</div>
          <p className="text-xs text-slate-500 mb-4">
            Start a new Opening Balance migration. You can save and resume at any time —
            nothing posts to the books until every validation passes and a second user approves.
          </p>
          <div className="flex gap-2">
            <input data-testid="ob-new-name" className={inputCls + " flex-1"} value={newName}
              onChange={(e) => setNewName(e.target.value)} placeholder="Batch name" />
            <button data-testid="ob-create-batch" onClick={createBatch} disabled={!canEdit}
              className={btnOrange + " flex items-center gap-1.5"}>
              <PlayCircle size={14} /> Start Migration
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-5 items-start">
          {/* Step rail */}
          <div className="w-72 shrink-0 bg-white border border-slate-200" data-testid="ob-step-rail">
            <div className="px-4 py-3 bg-[#0B1F3A]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Batch</div>
              <div className="text-sm font-bold text-white truncate">{batch.name}</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-700 overflow-hidden">
                  <div className="h-full bg-[#F97316] transition-all"
                    style={{ width: `${Math.round((doneCount / STEPS.length) * 100)}%` }} />
                </div>
                <span className="text-[10px] font-bold text-orange-300" data-testid="ob-progress">
                  {Math.round((doneCount / STEPS.length) * 100)}%
                </span>
              </div>
              <div className="mt-1.5 text-[10px] uppercase tracking-wider font-bold">
                <span className={
                  batch.status === "posted" ? "text-emerald-400"
                    : batch.status === "pending_approval" ? "text-amber-300" : "text-slate-300"
                } data-testid="ob-batch-status">{(batch.status || "").replace("_", " ")}</span>
              </div>
            </div>
            <div className="max-h-[62vh] overflow-y-auto">
              {STEPS.map((s) => {
                const done = stepDone(s.key);
                const Icon = s.icon;
                return (
                  <button key={s.key} data-testid={`ob-step-${s.key}`}
                    onClick={() => setActive(s.key)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-slate-100 text-left transition-colors ${
                      active === s.key ? "bg-orange-50 border-l-2 border-l-[#F97316]" : "hover:bg-slate-50"}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black shrink-0 ${
                      done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-600"}`}>
                      {done ? <CheckCircle2 size={12} /> : s.n}
                    </span>
                    <Icon size={13} className="text-slate-400 shrink-0" />
                    <span className={`text-xs ${active === s.key ? "font-bold text-slate-900" : "text-slate-600"}`}>{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step content */}
          <div className="flex-1 min-w-0">
            {STEPS.find((s) => s.key === active)?.category ? (
              <ObImportStep key={active} batch={batch} category={active}
                locked={locked || !canEdit} onChanged={load} />
            ) : active === "company" ? (
              <CompanyStep batch={batch} company={company} locked={locked || !canEdit} onChanged={load} />
            ) : active === "cutoff" ? (
              <CutoffStep batch={batch} locked={locked || !canEdit} onChanged={load} />
            ) : active === "exceptions" ? (
              <ExceptionsStep batch={batch} />
            ) : active === "tb" ? (
              <ObProjectionTB batch={batch} />
            ) : active === "bs" ? (
              <ObProjectionBS batch={batch} />
            ) : active === "pnl" ? (
              <ObProjectionPL batch={batch} />
            ) : (
              <PostStep batch={batch} canEdit={canEdit} canApprove={canApprove}
                isAdmin={user?.role === "admin"} userEmail={user?.email} onChanged={load} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StepCard({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200">
      <div className="px-5 py-3.5 border-b border-slate-100">
        <div className="text-sm font-bold text-slate-900">{title}</div>
        {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function CompanyStep({ batch, company, locked, onChanged }) {
  const info = batch.company_info || {};
  const [preparedBy, setPreparedBy] = useState(info.prepared_by || "");
  const [notes, setNotes] = useState(info.notes || "");
  async function confirm() {
    try {
      await api.put(`/opening-balance/batch/${batch.id}/company-info`,
        { prepared_by: preparedBy, notes, confirmed: true });
      toast.success("Company information confirmed");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <StepCard title="Step 1 — Company Information"
      subtitle="Verify the entity whose opening balances are being migrated (from Company Setup).">
      <div className="grid grid-cols-2 gap-3 max-w-2xl text-sm">
        {[["Company", company.company_name], ["Legal Name", company.legal_name],
          ["GSTIN", company.gstin], ["PAN", company.pan],
          ["CIN", company.cin], ["State", company.state]].map(([k, v]) => (
          <div key={k} className="border border-slate-100 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{k}</div>
            <div className="font-semibold text-slate-800" data-testid={`ob-company-${k.toLowerCase().replace(" ", "-")}`}>{v || "—"}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 max-w-2xl">
        <input data-testid="ob-prepared-by" className={inputCls} placeholder="Prepared by (name/designation)"
          value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} disabled={locked} />
        <input data-testid="ob-company-notes" className={inputCls} placeholder="Notes (certified TB reference, auditor, etc.)"
          value={notes} onChange={(e) => setNotes(e.target.value)} disabled={locked} />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button data-testid="ob-company-confirm" onClick={confirm} disabled={locked} className={btnPrimary}>
          {info.confirmed ? "Re-confirm" : "Confirm & Continue"}
        </button>
        {info.confirmed && <span className="text-xs text-emerald-600 font-bold flex items-center gap-1"><CheckCircle2 size={13} /> Confirmed</span>}
      </div>
    </StepCard>
  );
}

function CutoffStep({ batch, locked, onChanged }) {
  const [date, setDate] = useState(batch.cutoff_date || "");
  async function save() {
    try {
      const r = await api.put(`/opening-balance/batch/${batch.id}/cutoff`, { cutoff_date: date });
      toast.success("Cutoff date saved");
      if (r.data.warning) toast.warning(r.data.warning, { duration: 9000 });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <StepCard title="Step 2 — Cutoff Date"
      subtitle="All opening balances are certified as on this date; every opening GL entry is dated here. Recommended: the first day of the migration financial year (e.g. 2026-04-01).">
      <div className="flex items-center gap-3">
        <input data-testid="ob-cutoff-date" type="date" className={inputCls}
          value={date} onChange={(e) => setDate(e.target.value)} disabled={locked} />
        <button data-testid="ob-cutoff-save" onClick={save} disabled={locked || !date} className={btnPrimary}>
          Save Cutoff
        </button>
        {batch.cutoff_date && (
          <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
            <CheckCircle2 size={13} /> Set: {batch.cutoff_date}
          </span>
        )}
      </div>
      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mt-4 max-w-2xl">
        Changing the cutoff after imports requires re-uploading each category so date
        validations re-run against the new cutoff.
      </p>
    </StepCard>
  );
}

function ExceptionsStep({ batch }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/opening-balance/batch/${batch.id}/exceptions`)
      .then((r) => setData(r.data)).catch(() => toast.error("Failed to load exceptions"));
  }, [batch.id]);
  if (!data) return <StepCard title="Step 9 — Review Exceptions"><div className="text-sm text-slate-500">Loading…</div></StepCard>;
  return (
    <StepCard title="Step 9 — Review Exceptions"
      subtitle="Every row-level validation failure across all imports. Posting is blocked until this list is empty (fix the Excel and re-upload the category).">
      {data.total === 0 ? (
        <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-bold" data-testid="ob-exceptions-clear">
          <CheckCircle2 size={16} /> No open exceptions — all imported rows are valid
        </div>
      ) : (
        <div className="space-y-4" data-testid="ob-exceptions-list">
          <div className="text-xs font-bold text-rose-700">{data.total} exception(s) open</div>
          {Object.entries(data.by_category).filter(([, v]) => v.length).map(([cat, rows]) => (
            <div key={cat} className="border border-rose-200">
              <div className="px-3 py-2 bg-rose-50 text-[10px] uppercase tracking-wider font-bold text-rose-700">{cat} — {rows.length} row(s)</div>
              <div className="max-h-56 overflow-y-auto">
                {rows.map((r, i) => (
                  <div key={i} className="px-3 py-1.5 border-t border-rose-50 text-[11px] flex gap-2">
                    <AlertTriangle size={12} className="text-rose-500 mt-0.5 shrink-0" />
                    <span><b>Row {r.row_number}</b> — {(r.errors || []).join("; ")}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </StepCard>
  );
}

function PostStep({ batch, canEdit, canApprove, isAdmin, userEmail, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed", { duration: 8000 }); }
    finally { setBusy(false); }
  };
  const isMaker = batch.submitted_by === userEmail;
  const v = batch.verification || {};
  return (
    <StepCard title="Step 13 — Approve & Post"
      subtitle="Strict maker/checker: the user who submits cannot approve. Posting runs the OBC=0 gate again and writes documents + GL atomically.">
      {batch.status === "posted" ? (
        <div className="space-y-3" data-testid="ob-posted-panel">
          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-bold">
            <CheckCircle2 size={16} /> Posted — Voucher {batch.voucher_number} on {String(batch.posted_at || "").slice(0, 10)} by {batch.posted_by}
          </div>
          <div className="grid grid-cols-3 gap-2 max-w-2xl text-xs">
            {[["GL lines", v.gl_lines], ["GL Debit", v.gl_debit], ["GL Credit", v.gl_credit],
              ["OBC balance", v.obc_balance], ["Opening invoices", v.counts?.opening_invoices],
              ["Opening bills", v.counts?.opening_bills], ["Stock batches", v.counts?.inventory_batches],
              ["Assets enriched", v.counts?.assets_enriched]].map(([k, val]) => (
              <div key={k} className="border border-slate-100 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{k}</div>
                <div className="font-black text-slate-800 tabular-nums">{val ?? "—"}</div>
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="pt-3 border-t border-slate-100 flex items-center gap-2">
              <input data-testid="ob-rollback-reason" className={inputCls + " flex-1 max-w-md"}
                placeholder="Rollback reason (required, audited)" value={reason}
                onChange={(e) => setReason(e.target.value)} />
              <button data-testid="ob-rollback-btn" disabled={busy || !reason.trim()}
                onClick={() => act(() => api.post(`/opening-balance/batch/${batch.id}/rollback`, { reason }), "Batch rolled back")}
                className="border border-rose-300 text-rose-700 px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-rose-50 disabled:opacity-50 flex items-center gap-1.5">
                <RotateCcw size={12} /> Rollback Batch
              </button>
            </div>
          )}
        </div>
      ) : batch.status === "pending_approval" ? (
        <div className="space-y-3" data-testid="ob-pending-panel">
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 px-4 py-3">
            Submitted by <b>{batch.submitted_by}</b> on {String(batch.submitted_at || "").slice(0, 16).replace("T", " ")} — awaiting a different user (checker) to approve.
          </div>
          {canApprove && !isMaker ? (
            <div className="flex items-center gap-2">
              <button data-testid="ob-approve-post" disabled={busy}
                onClick={() => act(() => api.post(`/opening-balance/batch/${batch.id}/approve-post`), "Posted successfully")}
                className={btnOrange + " flex items-center gap-1.5"}>
                <ShieldCheck size={14} /> Approve & Post
              </button>
              <button data-testid="ob-reject" disabled={busy}
                onClick={() => act(() => api.post(`/opening-balance/batch/${batch.id}/reject`, { reason: "Returned by checker" }), "Returned to draft")}
                className="border border-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
                <X size={12} /> Return to Maker
              </button>
            </div>
          ) : (
            <div className="text-xs text-slate-500 font-semibold" data-testid="ob-maker-blocked">
              {isMaker ? "You submitted this batch — a different user must approve it (maker/checker)."
                : "You do not hold the approve permission for Opening Balance."}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-600 max-w-2xl">
            Submitting locks the batch for approval. The system re-validates: zero open
            exceptions, OBC residual = 0, no P&L accounts touched. A different user with the
            approve permission must then post it.
          </p>
          <button data-testid="ob-submit" disabled={busy || !canEdit}
            onClick={() => act(() => api.post(`/opening-balance/batch/${batch.id}/submit`), "Submitted for approval")}
            className={btnPrimary + " flex items-center gap-1.5"}>
            <ChevronRight size={14} /> Submit for Approval
          </button>
        </div>
      )}
    </StepCard>
  );
}
